import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Host, NgModule, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ConfirmDialogComponent, ConfirmDialogModule } from '@app/components/confirm-dialog/confirm-dialog.component';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { UserItemToRead } from '@app/models/user-item-to-read';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserItemsToReadService } from '@app/services/user-items-to-read.service';
import { BehaviorSubject, forkJoin, iif, Observable, of, Subscription } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-required-reading',
  templateUrl: './required-reading.component.html',
  styleUrls: ['./required-reading.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequiredReadingComponent implements OnInit, OnDestroy {

  readingItems$: Observable<Array<UserItemToRead>>;
  showHasRead: boolean;
  allowMarkAsReadFromReqReadList = this.featureSettingsStoreService.state.allowMarkAsReadFromReqReadList;
  private showHasReadSubject = new BehaviorSubject<boolean>(false);
  updateSubscription: Subscription;
  loading = false;

  constructor(private service: UserItemsToReadService,
              public elementRef: ElementRef,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private dialog: MatDialog,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective) {
  }

  handleShowHasRead(change: any) {
    this.showHasRead = change.checked;
    // key board right arrow event
    if (change.keyCode && change.keyCode === 39) {
      this.showHasRead = true;
    }
    // key board left arrow event
    if (change.keyCode && change.keyCode === 37) {
      this.showHasRead = false;
    }

    this.showHasReadSubject.next(this.showHasRead);
  }

  markAsRead(item: UserItemToRead) {

    const dialogRef = this.dialog.open(ConfirmDialogComponent);

    this.updateSubscription = dialogRef.afterClosed()
      .pipe(
        mergeMap(confirm => {
          return iif(() =>
            confirm,
            this.service.updateUserItem(item.ItemToRead.Guid, { ...item, ...{ HasRead: true } })
              .pipe(
                tap(() => {
                  this.showHasReadSubject.next(this.showHasRead);
                })),
            of(null));
        }))
      .subscribe();
  }

  ngOnInit() {
    this.loading = true;
    this.readingItems$ = this.showHasReadSubject
      .pipe(mergeMap((hasRead) => {
        if (hasRead) {
          return forkJoin([this.service.getAllUserItemsToRead(), this.service.getAllUserItemsHasRead()])
            .pipe(
              map(([r1, r2]) => r1.concat(r2)),
              tap(items => {
                if (items && items.length === 0) {
                  this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
                } else {
                  this.loading = false;
                  this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
                }
              })
            );
        } else {
          return this.service.getAllUserItemsToRead()
            .pipe(
              tap(items => {
                if (items && items.length === 0) {
                  this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
                } else {
                  this.loading = false;
                  this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
                }
              }));
        }
      }));
  }

  ngOnDestroy(): void {
    this.updateSubscription && this.updateSubscription.unsubscribe();
  }

}
@NgModule({
  declarations: [RequiredReadingComponent],
  exports: [RequiredReadingComponent],
  imports: [
    CommonModule,
    MatSlideToggleModule,
    LinkModule,
    MatButtonModule,
    ConfirmDialogModule,
    MatProgressSpinnerModule
  ]
})
export class RequiredReadingModule { }
